// Bulk profile assignment from `email,profileCode` rows.
//
// A pure parser, separate from the panel, so the validation rules that decide
// whether thousands of members get re-capped are unit-testable without a DOM.

export interface QuotaAssignmentCsvRow {
  line: number;
  email: string;
  profileCode: string;
  userId?: string;
  quotaProfileId?: string;
  /** i18n key. Present = the row is not applicable. */
  errorKey?: string;
}

export interface QuotaAssignmentCsvInputs {
  users: ReadonlyArray<{ id: string; email: string }>;
  profiles: ReadonlyArray<{ id: string; code: string; status: 'active' | 'inactive' }>;
}

/**
 * One row per non-empty line. A leading `email,profileCode` header is dropped.
 *
 * Every row is reported, valid or not: silently skipping a bad line would let
 * an operator believe the whole file applied. Duplicate emails are an error
 * rather than last-one-wins, because the file's two rows disagree and guessing
 * which the operator meant is exactly the wrong call at this scale.
 */
export function parseQuotaAssignmentCsv(
  text: string,
  { users, profiles }: QuotaAssignmentCsvInputs
): QuotaAssignmentCsvRow[] {
  const usersByEmail = new Map(users.map((user) => [user.email.trim().toLowerCase(), user]));
  const profilesByCode = new Map(
    profiles.map((profile) => [profile.code.trim().toLowerCase(), profile])
  );
  const seen = new Set<string>();

  return text
    .split(/\r?\n/)
    .map((raw, index) => ({ raw: raw.trim(), line: index + 1 }))
    .filter(({ raw }) => raw !== '')
    .flatMap(({ raw, line }, index) => {
      const [email = '', profileCode = ''] = raw
        .split(',')
        // Strip a UTF-8 BOM from the first field of the first line; Excel adds
        // one and it would make every email fail to match.
        .map((part, partIndex) =>
          (partIndex === 0 ? part.replace(/^\uFEFF/, '') : part).trim()
        );
      if (index === 0 && email.toLowerCase() === 'email' && profileCode.toLowerCase() === 'profilecode')
        return [];

      const user = usersByEmail.get(email.toLowerCase());
      const profile = profilesByCode.get(profileCode.toLowerCase());
      let errorKey: string | undefined;
      if (!email || !email.includes('@')) errorKey = 'quotaAssignments.csvInvalidEmail';
      else if (!profileCode) errorKey = 'quotaAssignments.csvInvalidProfile';
      else if (!user) errorKey = 'quotaAssignments.csvUserMissing';
      else if (!profile) errorKey = 'quotaAssignments.csvInvalidProfile';
      else if (profile.status !== 'active') errorKey = 'quotaAssignments.csvProfileInactive';
      else if (seen.has(user.id)) errorKey = 'quotaAssignments.csvDuplicateUser';

      if (user && profile && !errorKey) seen.add(user.id);
      return [
        {
          line,
          email,
          profileCode,
          ...(user ? { userId: user.id } : {}),
          ...(profile ? { quotaProfileId: profile.id } : {}),
          ...(errorKey ? { errorKey } : {}),
        },
      ];
    });
}
